import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Backdrop,
  CircularProgress,
  TextField,
  Button,
  Grid,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router";
import { useFactoring } from "../FactoringProvider";
import { usePreviousFactoring } from "../getPreviousFactoring";
import { getJaDisplayName } from "components/factoringUtil.ts";
import EntryDetail from "./EntryDetail.tsx";
import type { FactoringEntryInput } from "components/factoringModel";
import AnimationWrapper from "components/AnimationWrapper";
import IsShowField from "components/IsShowField";
import PrivacyPolicyInfo from "components/PrivacyPolicyInfo";
import useCanShowField from "./canShowField";
import { getLocalDateString } from "components/dateUtil";

export default function FactoringEntry() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: previousFactoring, loading, error } = usePreviousFactoring();
  const {
    factoring,
    setFactoring,
    entries,
    setEntries
  } = useFactoring();

  // Form validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false); // 送信試行フラグを追加

  // Use canShowField hook
  const canShow = useCanShowField(
    factoring.desiredAmount,
    entries,
    factoring.personalInfoConsent,
  );

  // Set previous factoring data when it's loaded, but only when coming from MyPage
  useEffect(() => {
    // Check if we're coming from MyPage using location state
    const isFromMyPage = location.state && location.state.from === "mypage";

    if (isFromMyPage) {
      if (previousFactoring) {
        setFactoring({
          desiredAmount: previousFactoring.desiredAmount,
          notes: previousFactoring.notes,
          personalInfoConsent: false,
        });
        setEntries(previousFactoring.entries.map((entry) => {
          return {
            ...entry,
            billingAmount: (entry as any).billingAmount ?? 0,
            paymentDate: (entry as any).paymentDate ?? "",
            imageFiles: [],
          }
        }));
      } else {
        handleAddEntry();
      }
    }

    if (error) {
      console.error("Error fetching previous factoring data:", error);
    }
  }, [previousFactoring, error, setFactoring, setEntries, location]);

  // Validate form fields
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Validate desired amount
    if (!Number.isFinite(factoring.desiredAmount) || factoring.desiredAmount <= 0) {
      newErrors.desiredAmount = "調達希望額を入力してください";
    } else if (!Number.isInteger(factoring.desiredAmount)) {
      newErrors.desiredAmount = "調達希望額は数字のみ入力可能です";
    }

    // Validate entries
    if (entries.length === 0) {
      newErrors.entries = "少なくとも1つの請求情報を追加してください";
    }

    // Validate required fields in each entry
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const entryPrefix = `entry_${i}`;

      // Required fields validation
      const requiredFields: (keyof FactoringEntryInput)[] = [
        "companyName",
        "prefecture",
        "city",
        "address1",
        "billingAmount",
        "paymentDate",
      ];

      for (const field of requiredFields) {
        if (!entry[field] || (typeof entry[field] === 'string' && !entry[field].trim())) {
          newErrors[`${entryPrefix}_${field}`] = `${getJaDisplayName(field)}を入力してください`;
        }
      }

      // Validate isInvoiceIssued is true
      if (!entry.isInvoiceIssued) {
        newErrors[`${entryPrefix}_isInvoiceIssued`] = "申し訳ございませんが未請求の請求書は買取できません";
      }

      // Validate images
      if (entry.imageFiles.length === 0) {
        newErrors[`${entryPrefix}_imageFiles`] = `書類のアップロードは必須です`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddEntry = () => {
    setEntries((prevEntries) => {
      const newEntry: FactoringEntryInput = {
        id: Date.now().toString(),
        companyName: "",
        corporateNumber: "",
        postalCode: "",
        prefecture: "",
        city: "",
        address1: "",
        address2: "",
        billingAmount: 0,
        paymentDate: getLocalDateString(new Date()),
        isInvoiceIssued: false,
        imageFiles: [],
      }
      return [...prevEntries, newEntry];
    });
  };

  // Handle removing an entry
  const handleRemoveEntry = (id: string) => {
    setEntries(entries.filter(entry => entry.id !== id));
  };

  // Handle submit button
  const handleSubmit = () => {
    setSubmitAttempted(true);

    if (!factoring.personalInfoConsent) {
      alert("個人情報の取り扱いへの同意が必要です");
      return;
    }
    if (!validateForm()) {
      return;
    }
    navigate("/factoring/confirm");
  };

  const hasValidationErrors = () => {
    return Object.keys(errors).length > 0;
  };

  const getMainErrorMessage = () => {
    if (errors.desiredAmount) return errors.desiredAmount;
    if (errors.entries) return errors.entries;

    // エントリー関連のエラーを検索
    const entryErrors = Object.entries(errors).filter(([key]) => key.startsWith('entry_'));
    if (entryErrors.length > 0) {
      return "請求情報に未入力または不正な項目があります。上記をご確認ください。";
    }

    return "入力内容にエラーがあります。上記をご確認ください。";
  };

  // Handle back button
  const handleBack = () => {
    if (!confirm("マイページへ戻りますか？")) {
      return;
    }
    navigate("/mypage");
  };

  // Fill test data (for development only)
  const fillTestData = () => {
    // Fill all fields at once
    setFactoring({ desiredAmount: 1000000, notes: "備考備考備考備考備考備考", personalInfoConsent: true });

    setEntries([{
      id: '0',
      companyName: "サンプル株式会社",
      corporateNumber: "1234567890123",
      postalCode: "123-4567",
      prefecture: "東京都",
      city: "千代田区",
      address1: "丸の内1-1-1",
      address2: "サンプルビル10階",
      billingAmount: 300000,
      paymentDate: "2025-12-31",
      isInvoiceIssued: true,
      imageFiles: [],
    }]);
  };

  // Render all form content
  const renderFormContent = () => {
    return (
      <>
        {/* Section 1: 調達希望額 */}
        <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
          調達希望額
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <IsShowField canShow={canShow('desiredAmount')}>
            <Grid size={12}>
              <AnimationWrapper delay={0}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="desiredAmount"
                  label={getJaDisplayName("desiredAmount")}
                  name="desiredAmount"
                  value={factoring.desiredAmount.toString()}
                  onChange={(e) => setFactoring((oldVal) => {
                    const onlyDigits = e.target.value.replace(/\D+/g, "");
                    const num = onlyDigits ? Number(onlyDigits) : 0;
                    return {
                      ...oldVal,
                      desiredAmount: num,
                    }
                  })}
                  error={!!errors.desiredAmount}
                  helperText={errors.desiredAmount || "数字のみ入力可能"}
                  slotProps={{
                    htmlInput: {
                      pattern: "[0-9]+",
                      inputMode: 'numeric'
                    },
                    input: {
                      endAdornment: <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>万円</Typography>,
                    },
                  }}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
        </Grid>

        {/* Section 2: 請求情報 */}
        <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
          請求情報
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {/* Billing information component */}
        {entries.map((entry, index) => (
          <React.Fragment key={entry.id}>
            <EntryDetail
              entry={entry}
              onChange={(newEntry) => {
                setEntries((prev) => prev.map(e => e.id === newEntry.id ? newEntry : e));
              }}
              onRemove={() => handleRemoveEntry(entry.id)}
              errors={errors}
              entryIndex={index}
              canShow={canShow}
            />
          </React.Fragment>
        ))}

        <IsShowField canShow={canShow('addEntryButton')}>
          <AnimationWrapper>
            <Box sx={{
              display: 'flex',
              flexDirection: {xs: 'column', sm: 'row'},
              alignItems: {xs: 'flex-start', sm: 'center'},
              gap: 1
            }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddEntry}
              >
                請求先追加
              </Button>
              <Typography variant="caption">
                ※2社以上の請求先がある場合はこちらをクリックしてください
              </Typography>
            </Box>
          </AnimationWrapper>
        </IsShowField>

        {/* Section 3: 備考 */}
        <Typography variant="h6" sx={{ mb: 2, mt: 6 }}>
          備考欄
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <IsShowField canShow={canShow('notes')}>
            <Grid size={12}>
              <AnimationWrapper delay={0}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="notes"
                  label={getJaDisplayName("notes")}
                  name="notes"
                  value={factoring.notes || ""}
                  onChange={(e) => setFactoring((oldVal) => ({
                    ...oldVal,
                    notes: e.target.value,
                  }))}
                  multiline
                  minRows={3}
                  placeholder="ご希望条件や連絡事項があればご記入ください（任意）"
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
        </Grid>

        {/* Personal Info Consent */}
        <IsShowField canShow={canShow('personalInfoConsent')}>
          <AnimationWrapper>
            <Box sx={{ mt: 5 }}>
              <PrivacyPolicyInfo />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={factoring.personalInfoConsent}
                    onChange={(e) =>
                      setFactoring((oldVal) => ({
                        ...oldVal,
                        personalInfoConsent: e.target.checked,
                      }))
                    }
                  />
                }
                label="個人情報の取り扱いに同意します"
              />
            </Box>
          </AnimationWrapper>
        </IsShowField>

        {/* Error message for entries */}
        {errors.entries && (
          <Typography color="error" variant="caption" sx={{ display: "block", mb: 2 }}>
            {errors.entries}
          </Typography>
        )}
      </>
    );
  };

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{
        mt: 4,
        mb: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box
        sx={{
          width: "100%",
        }}
      >
        <Typography
          component="h1"
          variant="h5"
          sx={{ mb: 2, textAlign: "center" }}
        >
          ファクタリング申込
        </Typography>

        {!loading && (
          <>
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Box component="form" noValidate>
                {renderFormContent()}

                <Box
                  sx={{ mt: 3, mb: 2 }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    {/* エラーメッセージをボタンの上に表示 */}
                    {submitAttempted && hasValidationErrors() && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mb: 1, textAlign: "right" }}
                      >
                        {getMainErrorMessage()}
                      </Typography>
                    )}
                    <IsShowField canShow={canShow('submitButton')}>
                      <AnimationWrapper>
                        <Button
                          onClick={handleSubmit}
                          variant="contained"
                          disabled={!factoring.personalInfoConsent}
                          color={submitAttempted && hasValidationErrors() ? "error" : "primary"}
                        >
                          確認画面へ
                        </Button>
                      </AnimationWrapper>
                    </IsShowField>
                  </Box>

                  <Box
                    sx={{ mt: 3, mb: 2 }}
                  >
                    <Button onClick={handleBack} variant="outlined">
                      戻る
                    </Button>
                  </Box>
                </Box>

                {process.env.NODE_ENV === "development" && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{ mb: 2 }}
                    onClick={fillTestData}
                  >
                    テストデータを入力
                  </Button>
                )}
                {process.env.NODE_ENV === "development" && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{ mb: 2 }}
                    onClick={() => {
                      throw new Error('This is your first error!');
                    }}
                  >
                    Break the world
                  </Button>
                )}
              </Box>
            </Paper>
          </>
        )}
      </Box>

    </Container>
  );
}
